import { useState } from 'react';

import { useDeleteParent } from '~entities/parent/api/delete-parent';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';

import { type ParentTableRow } from './types';

interface DeleteParentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedParents: Array<ParentTableRow>;
  onClose: () => void;
  isBulkDelete?: boolean;
  /** True when deleting every parent matching the current filters (not just the page). */
  isSelectAllMatching?: boolean;
  /** Total number of parents matching the current filters. */
  matchingCount?: number;
  onDelete?: () => void;
}

export const DeleteParentDialog = ({
  open,
  onOpenChange,
  selectedParents,
  onClose,
  isBulkDelete = false,
  isSelectAllMatching = false,
  matchingCount = 0,
  onDelete,
}: DeleteParentDialogProps) => {
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteMutation = useDeleteParent();

  const handleClose = () => {
    setDeleteConfirmText('');
    onClose();
  };

  // Check if any selected parent has students
  const hasParentsWithStudents = selectedParents.some(
    (parent) => parent.students.length > 0,
  );

  // Get details of parents with students
  const parentsWithStudents = selectedParents
    .filter((parent) => parent.students.length > 0)
    .map((parent) => ({
      name: parent.name,
      studentCount: parent.students.length,
    }));

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Escape, overlay click, and the dialog X bypass handleClose — reset
        // the confirmation text on every close path.
        if (!nextOpen) {
          setDeleteConfirmText('');
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Delete {isBulkDelete ? 'Parents' : 'Parent'}
          </DialogTitle>
        </DialogHeader>

        {isSelectAllMatching ? (
          <>
            <DialogDescription>
              You are about to delete all{' '}
              <span className='font-semibold'>{matchingCount}</span> parents
              matching the current filters, including their students. Parents
              with active enrollments, outstanding balances, or payments in
              progress will be skipped automatically. This action cannot be
              undone.
            </DialogDescription>
            <div className='mt-4'>
              <Input
                value={deleteConfirmText}
                onChange={(e) => {
                  setDeleteConfirmText(e.target.value);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                }}
                placeholder="Type 'DELETE' to confirm"
              />
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant='destructive'
                disabled={deleteConfirmText !== 'DELETE'}
                onClick={() => {
                  onDelete?.();
                  handleClose();
                }}
              >
                Delete {matchingCount} Parents
              </Button>
            </DialogFooter>
          </>
        ) : !hasParentsWithStudents ? (
          <>
            <DialogDescription>
              {isBulkDelete
                ? 'Are you sure you want to delete these parents? This action cannot be undone.'
                : 'Are you sure you want to delete this parent? This action cannot be undone.'}
            </DialogDescription>
            <div className='mt-4'>
              <Input
                value={deleteConfirmText}
                onChange={(e) => {
                  setDeleteConfirmText(e.target.value);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                }}
                placeholder="Type 'delete' to confirm"
              />
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant='destructive'
                disabled={deleteConfirmText !== 'delete'}
                onClick={() => {
                  if (isBulkDelete && onDelete) {
                    onDelete();
                  } else {
                    deleteMutation.mutate({
                      parentId: selectedParents[0]?._id ?? '',
                    });
                  }
                  handleClose();
                }}
              >
                {isBulkDelete ? 'Delete Parents' : 'Delete Parent'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogDescription className='text-destructive'>
              {isBulkDelete
                ? 'The following parents have students and cannot be deleted:'
                : 'This parent has students and cannot be deleted:'}
              <ul className='mt-2 list-disc pl-4'>
                {parentsWithStudents.map((parent) => (
                  <li key={parent.name}>
                    <span className='font-medium text-black'>
                      {parent.name}
                    </span>
                    : {parent.studentCount}{' '}
                    {parent.studentCount === 1 ? 'student' : 'students'}
                  </li>
                ))}
              </ul>
              Please remove all students before deleting{' '}
              {isBulkDelete ? 'these parents' : 'this parent'}.
            </DialogDescription>
            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
